/**
 * ApplyConfirmationModal — WO-17 PR-C  +  TASK 10-1 share card  +  TASK 10-2 testimonial
 *
 * Shown after a user clicks "Optimize & Apply" on a Peerlist job and
 * returns from the optimizer. Lets them record the outcome:
 *
 *   Applied           → application_status = 'applied'
 *   Got Interview 🗓  → application_status = 'interview_scheduled'
 *   Got Hired! 🎉     → application_status = 'offered' + share card + testimonial
 *
 * TASK 10-1 additions (share card flow):
 *   step 'generating' — calls the generate-share-card edge function
 *   step 'share'      — shows preview image + copy / social / download buttons
 *
 * TASK 10-2 additions (testimonial collection):
 *   step 'testimonial' — "Want to share your story?" → textarea → INSERT
 *                        with approved=false (hard rule: never self-approve)
 *
 * TASK 11-2 (Google review prompt):
 *   step 'google_review' — after share (and optional testimonial), if
 *   last_review_prompt_at is NULL or older than 14 days. Sequenced so
 *   testimonial and Google prompts never appear at the same time.
 *   Triggered only from outcome hired (offer), never standalone.
 */
import { AnimatePresence, motion } from 'framer-motion';
import {
  Briefcase,
  Calendar,
  Check,
  Copy,
  Download,
  ExternalLink,
  MessageSquare,
  PartyPopper,
  Share2,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

import LoadingSpinner from '../../../../components/LoadingSpinner';
import {
  fetchShouldShowGoogleReviewPrompt,
  GoogleReviewPromptPanel,
  openGoogleMapsReview,
  snoozeGoogleReviewPrompt,
} from '../../../../components/GoogleReviewPrompt';
import { supabase } from '../../../../lib/supabase';
import type { Job } from '../JobCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApplyConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  applicationId?: string | null;
}

type OutcomeKind  = 'applied' | 'interview' | 'hired';
type ModalStep    = 'outcome' | 'generating' | 'share' | 'testimonial' | 'google_review';

interface OutcomeOption {
  kind:        OutcomeKind;
  icon:        React.ReactNode;
  label:       string;
  description: string;
  accentClass: string;
}

interface ShareCard {
  slug:       string;
  image_url:  string;
  public_url: string;
}

const MAX_QUOTE = 500;
const MIN_QUOTE = 20;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_MAP: Record<OutcomeKind, string> = {
  applied:   'applied',
  interview: 'interview_scheduled',
  hired:     'offered',
};

// ---------------------------------------------------------------------------
// Share helpers
// ---------------------------------------------------------------------------

function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard) return navigator.clipboard.writeText(text);
  const el = document.createElement('textarea');
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
  return Promise.resolve();
}

function shareLinks(publicUrl: string, headline: string) {
  const encoded = encodeURIComponent(publicUrl);
  const text    = encodeURIComponent(headline + ' 🎉 #GoGrabJob #Hired');
  return {
    twitter:   `https://twitter.com/intent/tweet?url=${encoded}&text=${text}`,
    linkedin:  `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`,
    whatsapp:  `https://wa.me/?text=${text}%20${encoded}`,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ApplyConfirmationModal: React.FC<ApplyConfirmationModalProps> = ({
  isOpen,
  onClose,
  job,
  applicationId,
}) => {
  const { t } = useTranslation();
  const [saving,    setSaving]    = useState<OutcomeKind | null>(null);
  const [step,      setStep]      = useState<ModalStep>('outcome');
  const [shareCard, setShareCard] = useState<ShareCard | null>(null);
  const [genError,  setGenError]  = useState<string | null>(null);
  const [copied,    setCopied]    = useState(false);
  const [toast,     setToast]     = useState<string | null>(null);

  // Testimonial step state
  const [tQuote,       setTQuote]       = useState('');
  const [tDisplayName, setTDisplayName] = useState('');
  const [tSubmitting,  setTSubmitting]  = useState(false);
  const [tSubmitted,   setTSubmitted]   = useState(false);
  const [tError,       setTError]       = useState<string | null>(null);
  const [googleBusy,   setGoogleBusy]   = useState(false);

  const toastTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const outcomeOptions = useMemo<OutcomeOption[]>(() => [
    {
      kind:        'applied',
      icon:        <Briefcase className="w-6 h-6 shrink-0" />,
      label:       t('dashboard.jobs.applyOutcome.appliedLabel', 'Applied'),
      description: t('dashboard.jobs.applyOutcome.appliedDesc', "I submitted my application. I'll update when I hear back."),
      accentClass: 'ds-recessed',
    },
    {
      kind:        'interview',
      icon:        <Calendar className="w-6 h-6 shrink-0" />,
      label:       t('dashboard.jobs.applyOutcome.interviewLabel', 'Got Interview 🗓'),
      description: t('dashboard.jobs.applyOutcome.interviewDesc', "They got back to me — I have an interview scheduled!"),
      accentClass: 'ds-recessed-info',
    },
    {
      kind:        'hired',
      icon:        <PartyPopper className="w-6 h-6 shrink-0" />,
      label:       t('dashboard.jobs.applyOutcome.hiredLabel', 'Got Hired! 🎉'),
      description: t('dashboard.jobs.applyOutcome.hiredDesc', "I got the job! Let's celebrate and share your win."),
      accentClass: 'ds-recessed-ok',
    },
  ], [t]);

  const shareHeadline = useMemo(
    () => t('dashboard.jobs.applyOutcome.shareHeadline', { defaultValue: 'I landed a job at {{company}}!', company: job.company }),
    [t, job.company],
  );

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('outcome');
      setShareCard(null);
      setGenError(null);
      setCopied(false);
      setSaving(null);
      setToast(null);
      setTQuote('');
      setTDisplayName('');
      setTSubmitting(false);
      setTSubmitted(false);
      setTError(null);
      setGoogleBusy(false);
    }
  }, [isOpen]);

  useEffect(() => () => {
    if (toastTimerRef.current)  clearTimeout(toastTimerRef.current);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  }, []);

  // ── TASK 11-2: after share / testimonial, optionally show Google review ─────

  const advanceAfterOfferSubflow = useCallback(async () => {
    try {
      const show = await fetchShouldShowGoogleReviewPrompt();
      if (show) setStep('google_review');
      else onClose();
    } catch {
      onClose();
    }
  }, [onClose]);

  const handleGoogleLeaveReview = useCallback(() => {
    openGoogleMapsReview();
    onClose();
  }, [onClose]);

  const handleGoogleMaybeLater = useCallback(async () => {
    setGoogleBusy(true);
    try {
      await snoozeGoogleReviewPrompt();
    } finally {
      setGoogleBusy(false);
      onClose();
    }
  }, [onClose]);

  // ── Generate share card via edge function ─────────────────────────────────

  const generateShareCard = useCallback(async () => {
    setStep('generating');
    setGenError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/generate-share-card`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          role:    job.title,
          company: job.company,
          // headline defaults to "I landed a job at {company}!" on the server
        }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errBody.error ?? `Server error ${res.status}`);
      }

      const card = await res.json() as ShareCard;
      setShareCard(card);
      setStep('share');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate share card';
      console.warn('[ApplyConfirmationModal] share card error:', msg);
      setGenError(msg);
      // Fall back to outcome step so user isn't stuck
      setStep('outcome');
      showToast(t('dashboard.jobs.applyOutcome.shareGenFailed', 'Could not generate share card — your win is still saved!'));
    }
  }, [job, showToast, t]);

  // ── Pre-fill display name from session ───────────────────────────────────

  useEffect(() => {
    if (step === 'testimonial' && !tDisplayName) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        const name =
          (session?.user?.user_metadata?.full_name as string | undefined) ??
          (session?.user?.user_metadata?.name     as string | undefined) ??
          '';
        if (name) setTDisplayName(name);
      });
    }
  // Only run when entering the testimonial step
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ── Submit testimonial ────────────────────────────────────────────────────

  const handleTestimonialSubmit = useCallback(async () => {
    const quote = tQuote.trim();
    const displayName = tDisplayName.trim();
    if (quote.length < MIN_QUOTE) {
      setTError(t('dashboard.jobs.applyOutcome.quoteMinLength', { defaultValue: 'Please write at least {{min}} characters.', min: MIN_QUOTE }));
      return;
    }
    if (!displayName) {
      setTError(t('dashboard.jobs.applyOutcome.displayNameRequired', 'Please enter your display name.'));
      return;
    }
    setTSubmitting(true);
    setTError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');

      const { error } = await supabase.from('testimonials').insert({
        user_id:           session.user.id,
        quote,
        user_display_name: displayName,
        role_landed:       job.title,
        company_name:      job.company,
        // approved stays false (server default) — hard rule
      });
      if (error) throw error;

      setTSubmitted(true);
    } catch (err) {
      setTError(err instanceof Error ? err.message : t('dashboard.jobs.applyOutcome.testimonialSubmitFailed', 'Failed to submit — please try again.'));
    } finally {
      setTSubmitting(false);
    }
  }, [tQuote, tDisplayName, job, t]);

  // ── Handle outcome selection ──────────────────────────────────────────────

  const handleSelect = useCallback(
    async (kind: OutcomeKind) => {
      setSaving(kind);
      try {
        if (applicationId) {
          const update: Record<string, unknown> = { application_status: STATUS_MAP[kind] };
          if (kind === 'hired') update.hired_at = new Date().toISOString();

          const { error } = await supabase
            .from('job_applications')
            .update(update)
            .eq('id', applicationId);

          if (error) {
            console.warn('[ApplyConfirmationModal] update failed:', error.message);
            showToast(t('dashboard.jobs.applyOutcome.saveStatusFailed', 'Could not save status — please update it manually in My Activities.'));
            setSaving(null);
            return;
          }
        }

        setSaving(null);

        if (kind === 'hired') {
          // TASK 10-1: Transition to share card generation instead of closing
          await generateShareCard();
        } else {
          const msgs: Record<OutcomeKind, string> = {
            applied:   t('dashboard.jobs.applyOutcome.toastApplied', { defaultValue: 'Logged as "Applied" for {{title}} at {{company}}.', title: job.title, company: job.company }),
            interview: t('dashboard.jobs.applyOutcome.toastInterview', { defaultValue: 'Interview logged for {{title}} at {{company}}. Good luck!', title: job.title, company: job.company }),
            hired:     '',
          };
          showToast(msgs[kind]);
          setTimeout(onClose, 800);
        }
      } catch (err) {
        console.warn('[ApplyConfirmationModal] unexpected error:', err);
        showToast(t('dashboard.jobs.applyOutcome.genericError', 'Something went wrong — please try again.'));
        setSaving(null);
      }
    },
    [applicationId, job, onClose, showToast, generateShareCard, t],
  );

  const handleSkip = useCallback(() => { onClose(); }, [onClose]);

  // ── Share actions ─────────────────────────────────────────────────────────

  const handleCopy = useCallback(async () => {
    if (!shareCard) return;
    await copyToClipboard(shareCard.public_url);
    setCopied(true);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 2200);
  }, [shareCard]);

  const handleDownload = useCallback(() => {
    if (!shareCard) return;
    const a = document.createElement('a');
    a.href     = shareCard.image_url;
    a.download = `gograbjo-win-${shareCard.slug}.svg`;
    a.target   = '_blank';
    a.rel      = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [shareCard]);

  // ── Render steps ──────────────────────────────────────────────────────────

  const renderOutcomeStep = () => (
    <>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
        <div className="min-w-0">
          <h2 className="t-h3">
            {t('dashboard.jobs.applyOutcome.howDidItGo', 'How did it go?')}
          </h2>
          <p className="t-meta mt-0.5 truncate">
            {job.title} · {job.company}
          </p>
        </div>
        <button
          type="button"
          onClick={handleSkip}
          aria-label={t('dashboard.jobs.applyOutcome.close', 'Close')}
          className="ds-btn ds-btn-ghost ds-btn-sm shrink-0 touch-manipulation"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Options */}
      <div className="flex flex-col gap-2.5 px-5 py-4">
        {outcomeOptions.map((opt) => {
          const isBusy = saving === opt.kind;
          return (
            <button
              key={opt.kind}
              type="button"
              disabled={saving !== null}
              onClick={() => handleSelect(opt.kind)}
              className={`
                w-full min-h-[56px] flex items-center gap-3 px-4 py-3
                text-left transition-all touch-manipulation
                disabled:opacity-50 disabled:cursor-not-allowed
                ${opt.accentClass}
              `}
            >
              <span className="t-strong">{opt.icon}</span>
              <span className="flex-1 min-w-0">
                <span className="block t-card-title">
                  {isBusy ? t('dashboard.jobs.applyOutcome.saving', 'Saving…') : opt.label}
                </span>
                <span className="block t-meta mt-0.5">
                  {opt.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-5 pb-5 pt-1">
        <button
          type="button"
          onClick={handleSkip}
          disabled={saving !== null}
          className="ds-btn ds-btn-ghost w-full min-h-[44px] touch-manipulation disabled:opacity-40"
        >
          {t('dashboard.jobs.applyOutcome.skipLater', "Skip — I'll update this later")}
        </button>
      </div>
    </>
  );

  const renderGeneratingStep = () => (
    <div className="flex flex-col items-center justify-center gap-4 px-6 py-12 text-center">
      <LoadingSpinner size="lg" showText={false} />
      <div>
        <p className="t-h3">
          {t('dashboard.jobs.applyOutcome.generatingTitle', 'Generating your share card…')}
        </p>
        <p className="t-meta mt-1">
          {t('dashboard.jobs.applyOutcome.generatingSubtitle', { defaultValue: 'Creating a branded card for your win at {{company}} 🎉', company: job.company })}
        </p>
      </div>
    </div>
  );

  const renderShareStep = () => {
    if (!shareCard) return null;
    const links = shareLinks(shareCard.public_url, shareHeadline);

    return (
      <>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
          <div className="min-w-0">
            <h2 className="t-h3 flex items-center gap-2">
              <PartyPopper className="w-5 h-5 shrink-0" style={{ color: 'var(--ds-ok)' }} />
              {t('dashboard.jobs.applyOutcome.shareTitle', 'Share your win! 🎉')}
            </h2>
            <p className="t-meta mt-0.5 truncate">
              {job.title} · {job.company}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void advanceAfterOfferSubflow()}
            aria-label={t('dashboard.jobs.applyOutcome.close', 'Close')}
            className="ds-btn ds-btn-ghost ds-btn-sm shrink-0 touch-manipulation"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Preview */}
        <div className="px-5 pt-4">
          <a
            href={shareCard.public_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ds-card-flush block overflow-hidden transition-shadow"
          >
            <img
              src={shareCard.image_url}
              alt={t('dashboard.jobs.applyOutcome.shareCardAlt', { defaultValue: 'Share card: I landed a job at {{company}}', company: job.company })}
              className="w-full object-cover"
              style={{ aspectRatio: '1200/630' }}
            />
          </a>
          <p className="mt-2 t-meta text-center flex items-center justify-center gap-1">
            <ExternalLink className="w-3 h-3" />
            <a
              href={shareCard.public_url}
              target="_blank"
              rel="noopener noreferrer"
              className="t-link truncate max-w-[260px]"
            >
              {shareCard.public_url}
            </a>
          </p>
        </div>

        {/* Copy link */}
        <div className="px-5 pt-3">
          <button
            type="button"
            onClick={handleCopy}
            className="ds-btn ds-btn-secondary w-full min-h-[44px] touch-manipulation"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" style={{ color: 'var(--ds-ok)' }} />
                {t('dashboard.jobs.applyOutcome.copied', 'Copied!')}
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                {t('dashboard.jobs.applyOutcome.copyLink', 'Copy link')}
              </>
            )}
          </button>
        </div>

        {/* Social share buttons */}
        <div className="px-5 pt-2 grid grid-cols-3 gap-2">
          {/* Twitter/X */}
          <a
            href={links.twitter}
            target="_blank"
            rel="noopener noreferrer"
            className="ds-btn ds-btn-secondary flex-col min-h-[56px] h-auto touch-manipulation"
          >
            <Share2 className="w-4 h-4" />
            <span>{t('dashboard.jobs.applyOutcome.twitter', 'X / Twitter')}</span>
          </a>

          {/* LinkedIn */}
          <a
            href={links.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="ds-btn ds-btn-secondary flex-col min-h-[56px] h-auto touch-manipulation"
          >
            <ExternalLink className="w-4 h-4" />
            <span>{t('dashboard.jobs.applyOutcome.linkedin', 'LinkedIn')}</span>
          </a>

          {/* WhatsApp */}
          <a
            href={links.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="ds-btn ds-btn-secondary flex-col min-h-[56px] h-auto touch-manipulation"
          >
            <Share2 className="w-4 h-4" />
            <span>{t('dashboard.jobs.applyOutcome.whatsapp', 'WhatsApp')}</span>
          </a>
        </div>

        {/* Tell your story CTA (TASK 10-2) */}
        <div className="px-5 pb-2">
          <button
            type="button"
            onClick={() => setStep('testimonial')}
            className="ds-btn ds-btn-primary w-full min-h-[44px] touch-manipulation"
          >
            <MessageSquare className="w-4 h-4" />
            {t('dashboard.jobs.applyOutcome.tellStory', 'Tell your story →')}
          </button>
        </div>

        {/* Download + Close */}
        <div className="px-5 pb-5 pt-2 flex gap-2">
          <button
            type="button"
            onClick={handleDownload}
            className="ds-btn ds-btn-secondary flex-1 min-h-[44px] touch-manipulation"
          >
            <Download className="w-3.5 h-3.5" />
            {t('dashboard.jobs.applyOutcome.download', 'Download')}
          </button>
          <button
            type="button"
            onClick={() => void advanceAfterOfferSubflow()}
            className="ds-btn ds-btn-ghost flex-1 min-h-[44px] touch-manipulation"
          >
            {t('dashboard.jobs.applyOutcome.maybeLater', 'Maybe later')}
          </button>
        </div>
      </>
    );
  };

  // ── Testimonial step ──────────────────────────────────────────────────────

  const renderTestimonialStep = () => (
    <>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
        <div className="min-w-0">
          <h2 className="t-h3 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 shrink-0" style={{ color: 'var(--ds-brand)' }} />
            {t('dashboard.jobs.applyOutcome.testimonialTitle', 'Want to share your story?')}
          </h2>
          <p className="t-meta mt-0.5">
            {t('dashboard.jobs.applyOutcome.testimonialSubtitle', "Your review helps others who are job-hunting. We'll review before publishing.")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void advanceAfterOfferSubflow()}
          aria-label={t('dashboard.jobs.applyOutcome.close', 'Close')}
          className="ds-btn ds-btn-ghost ds-btn-sm shrink-0 touch-manipulation"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {tSubmitted ? (
        /* ── Thank you state ── */
        <div className="flex flex-col items-center gap-4 px-6 py-10 text-center">
          <span className="text-4xl">🙏</span>
          <div>
            <p className="t-h3">{t('dashboard.jobs.applyOutcome.thankYou', 'Thank you!')}</p>
            <p className="t-body mt-1 max-w-xs">
              {t('dashboard.jobs.applyOutcome.testimonialSubmitted', "Your story has been submitted for review. We'll feature it on the GoGrabJob homepage once approved.")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void advanceAfterOfferSubflow()}
            className="ds-btn ds-btn-primary mt-2 touch-manipulation"
          >
            {t('dashboard.jobs.applyOutcome.close', 'Close')}
          </button>
        </div>
      ) : (
        /* ── Form state ── */
        <div className="flex flex-col gap-3 px-5 py-4">
          {/* Pre-filled role + company (read-only context) */}
          <div className="ds-recessed flex gap-2 text-xs px-3 py-2">
            <span className="font-medium">{t('dashboard.jobs.applyOutcome.roleLabel', 'Role:')}</span>
            <span className="truncate">{job.title} at {job.company}</span>
          </div>

          {/* Display name */}
          <div>
            <label className="block t-label mb-1">
              {t('dashboard.jobs.applyOutcome.displayNameLabel', 'Your display name *')}
            </label>
            <input
              type="text"
              value={tDisplayName}
              onChange={e => setTDisplayName(e.target.value)}
              placeholder={t('dashboard.jobs.applyOutcome.displayNamePlaceholder', 'e.g. Priya S. (can be anonymous)')}
              maxLength={80}
              disabled={tSubmitting}
              className="ds-input disabled:opacity-50"
            />
          </div>

          {/* Quote textarea */}
          <div>
            <label className="block t-label mb-1">
              {t('dashboard.jobs.applyOutcome.testimonialLabel', 'Your testimonial *')}{' '}
              <span className="font-normal opacity-60">({MIN_QUOTE}–{MAX_QUOTE} chars)</span>
            </label>
            <textarea
              value={tQuote}
              onChange={e => setTQuote(e.target.value)}
              placeholder={t('dashboard.jobs.applyOutcome.testimonialPlaceholder', 'How did GoGrabJob help you land this role? What feature made the difference?')}
              rows={4}
              maxLength={MAX_QUOTE}
              disabled={tSubmitting}
              className="ds-textarea resize-none disabled:opacity-50"
            />
            <p className={`t-tag text-right mt-0.5 ${tQuote.length > MAX_QUOTE - 20 ? 't-warn' : 't-muted'}`}>
              {tQuote.length} / {MAX_QUOTE}
            </p>
          </div>

          {/* Error */}
          {tError && (
            <p className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg" role="alert">
              {tError}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => void handleTestimonialSubmit()}
              disabled={tSubmitting || tQuote.trim().length < MIN_QUOTE || !tDisplayName.trim()}
              className="ds-btn ds-btn-primary flex-1 min-h-[44px] touch-manipulation disabled:opacity-40"
            >
              {tSubmitting ? (
                <><LoadingSpinner size="xs" showText={false} /> {t('dashboard.jobs.applyOutcome.submitting', 'Submitting…')}</>
              ) : (
                t('dashboard.jobs.applyOutcome.submitStory', 'Submit my story')
              )}
            </button>
            <button
              type="button"
              onClick={() => void advanceAfterOfferSubflow()}
              disabled={tSubmitting}
              className="ds-btn ds-btn-ghost px-4 min-h-[44px] touch-manipulation disabled:opacity-40"
            >
              {t('dashboard.jobs.applyOutcome.maybeLater', 'Maybe later')}
            </button>
          </div>
        </div>
      )}
    </>
  );

  const renderGoogleReviewStep = () => (
    <GoogleReviewPromptPanel
      onLeaveReview={handleGoogleLeaveReview}
      onMaybeLater={handleGoogleMaybeLater}
      busy={googleBusy}
    />
  );

  const dialogAriaLabel =
    step === 'share'        ? t('dashboard.jobs.applyOutcome.ariaShare', 'Share your win') :
    step === 'testimonial'  ? t('dashboard.jobs.applyOutcome.ariaTestimonial', 'Share your story') :
    step === 'google_review' ? t('dashboard.jobs.applyOutcome.ariaGoogleReview', 'Google review') :
    t('dashboard.jobs.applyOutcome.ariaOutcome', 'How did your application go?');

  const handleBackdropClick = useCallback(() => {
    if (step === 'generating') return;
    if (step === 'google_review') {
      void handleGoogleMaybeLater();
      return;
    }
    if (step === 'share' || step === 'testimonial') {
      void advanceAfterOfferSubflow();
      return;
    }
    handleSkip();
  }, [step, handleGoogleMaybeLater, advanceAfterOfferSubflow, handleSkip]);

  // ── Overlay (portal) ──────────────────────────────────────────────────────

  const overlay = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="acm-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-gray-900/70 backdrop-blur-sm"
            onClick={handleBackdropClick}
            aria-hidden="true"
          />

          {/* Panel */}
          <motion.div
            key="acm-panel"
            role="dialog"
            aria-modal="true"
            aria-label={dialogAriaLabel}
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="fixed inset-0 z-[9999] flex items-center justify-center px-4 pointer-events-none"
          >
            <div
              className={`
                pointer-events-auto w-full ds-card-pop overflow-y-auto max-h-[90vh] flex flex-col p-0
                ${step === 'share' ? 'max-w-lg' : 'max-w-md'}
              `}
            >
              {step === 'outcome'      && renderOutcomeStep()}
              {step === 'generating'   && renderGeneratingStep()}
              {step === 'share'        && renderShareStep()}
              {step === 'testimonial'  && renderTestimonialStep()}
              {step === 'google_review' && renderGoogleReviewStep()}
            </div>
          </motion.div>

          {/* Toast */}
          {toast && (
            <div
              key="acm-toast"
              className="fixed right-4 z-[10000] max-w-sm ds-card-pop pointer-events-none"
              style={{ bottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
            >
              {toast}
            </div>
          )}

          {/* Error */}
          {genError && (
            <div className="fixed bottom-16 right-4 z-[10000] max-w-sm ds-recessed-danger pointer-events-none">
              {genError}
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(overlay, document.body);
  }
  return null;
};

export default ApplyConfirmationModal;
